import axios from 'axios';

export interface LocationSuggestion {
  displayName: string;
  lat: number;
  lon: number;
  city: string;
  country: string;
}

export interface RouteDistance {
  distanceKm: number;
  durationMin: number;
}

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';
const OSRM_BASE_URL = 'https://router.project-osrm.org';

const ALLOWED_COUNTRIES = [
  'Tanzania',
  'Kenya',
  'Uganda',
  'Rwanda',
  'Burundi',
  'Democratic Republic of the Congo',
  'Republic of the Congo',
  'Congo',
  'Zambia',
  'Malawi',
  'Mozambique',
  'Sudan',
  'South Sudan'
];

class GeocodingService {
  async searchCities(query: string): Promise<LocationSuggestion[]> {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const response = await axios.get(`${NOMINATIM_BASE_URL}/search`, {
        params: {
          q: query,
          format: 'json',
          addressdetails: 1,
          limit: 10,
          'accept-language': 'en',
          countrycodes: 'tz,ke,ug,rw,bi,et,so,ss,sd,er,dj,mz,mw,zm,zw,bw,na,za,ls,sz,ao,cd,cg,ga,gq,cm,cf,td,ng,ne,bj,tg,gh,bf,ml,mr,sn,gm,gw,gn,sl,lr,ci,mg,mu,sc,km',
        },
        headers: {
          'User-Agent': 'TransportERP/1.0',
        },
      });

      const results = response.data
        .filter((item: any) => {
          const placeType = item.type || '';
          const placeClass = item.class || '';
          return (
            (placeClass === 'place' && ['city', 'town', 'village'].includes(placeType)) ||
            (placeClass === 'boundary' && placeType === 'administrative')
          );
        })
        .map((item: any) => ({
          displayName: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
          city: item.address?.city || item.address?.town || item.address?.village || item.name,
          country: item.address?.country || '',
        }))
        .filter((item: LocationSuggestion) => 
          ALLOWED_COUNTRIES.some(country => 
            item.country.toLowerCase().includes(country.toLowerCase())
          )
        )
        .sort((a: LocationSuggestion, b: LocationSuggestion) => {
          const aTanzania = a.country.toLowerCase().includes('tanzania');
          const bTanzania = b.country.toLowerCase().includes('tanzania');
          if (aTanzania && !bTanzania) return -1;
          if (!aTanzania && bTanzania) return 1;
          return 0;
        });

      return results.slice(0, 8);
    } catch (error) {
      console.error('Geocoding search error:', error);
      return [];
    }
  }

  async calculateDistance(
    originLat: number,
    originLon: number,
    destLat: number,
    destLon: number
  ): Promise<RouteDistance | null> {
    try {
      const response = await axios.get(
        `${OSRM_BASE_URL}/route/v1/driving/${originLon},${originLat};${destLon},${destLat}`,
        {
          params: {
            overview: false,
            steps: false,
          },
        }
      );

      if (response.data.code === 'Ok' && response.data.routes?.[0]) {
        const route = response.data.routes[0];
        return {
          distanceKm: Math.round(route.distance / 1000 * 10) / 10,
          durationMin: Math.round(route.duration / 60),
        };
      }

      return null;
    } catch (error) {
      console.error('Distance calculation error:', error);
      return null;
    }
  }
}

export const geocodingService = new GeocodingService();
